/* eslint-disable warp-drive/no-legacy-request-patterns */
import { get } from '@ember/object';
import { byKeys, findBy } from '@linc-technologies/linced-common/utils/arrays';
import { getCurrentMilestone } from '@linc-technologies/linced-common/utils/progressions';
import reportStudentName from '@linc-technologies/linced-common/utils/report-student-name';
import {
  AUTO_GENERATED_FLAG_MAP,
  COMPONENT_FIELD_MAP,
  escapeHtml,
  hasText,
  personaliseRubricText,
  resolveRubricElements,
  shuffle,
} from 'linced-app/utils/reporting';

// Default wording for the bold opening line of a generated teacher comment.
// A report template can replace any of these (judgementWording); FIRSTNAME
// and the pronoun keys are filled in as in rubric wording.
export const DEFAULT_JUDGEMENT_WORDING = Object.freeze({
  interimOnTrack: 'FIRSTNAME is on track to meet expectations',
  interimNotOnTrack: 'FIRSTNAME is not yet on track to meet expectations',
  finalMet: 'FIRSTNAME has met expectations',
  finalNotMet: 'FIRSTNAME has not met expectations',
});

/**
 * The opening line's wording for a mid-year (interim) or end-of-year report
 * where the student is or isn't on track: the template's phrase, or the
 * default when it hasn't set one.
 */
export function judgementWording(template, isInterim, isOnTrack) {
  let key;
  if (isInterim) {
    key = isOnTrack ? 'interimOnTrack' : 'interimNotOnTrack';
  } else {
    key = isOnTrack ? 'finalMet' : 'finalNotMet';
  }
  let custom = template && get(template, `judgementWording.${key}`);
  return (custom && custom.trim()) || DEFAULT_JUDGEMENT_WORDING[key];
}

// Narrative fragments picked per strand. Each strand gets its own
// paragraph, so strands are never mixed together.
const NARRATIVE_FRAGMENTS_PER_STRAND = 2;

// Splits narrative wording into sentences. Common abbreviations like "e.g."
// aren't sentence ends.
const SENTENCE_BREAK = /(?<!\b(?:e\.g|i\.e|etc|vs)\.)(?<=[.!?])\s+/;

// Content saved before HERO-13622 wrapped generated text in these markers.
// A field that is nothing but a marked block is still untouched generated
// text, so it may be regenerated even though its auto-generated flag was
// never set. Tested against the trimmed field, so there is no trailing
// whitespace to match and each empty paragraph is anchored by its <p>, which
// keeps the check linear on long or malformed content.
const LEGACY_AUTO_BLOCK =
  /^<!-- rubric-auto -->[\s\S]*?<!-- \/rubric-auto -->(?:\s*<p>\s*<br\s*\/?>\s*<\/p>)*$/;

/**
 * Whether the rubric may (re)write componentType's field on section: the
 * field is empty, still holds untouched generated text, or is a legacy
 * marked block.
 */
export function canAutoPopulate(section, componentType) {
  let field = COMPONENT_FIELD_MAP[componentType];
  if (!section || !field) {
    return false;
  }
  let existing = section.get(field) || '';
  return (
    !hasText(existing) ||
    !!section.get(AUTO_GENERATED_FLAG_MAP[field]) ||
    LEGACY_AUTO_BLOCK.test(existing.trim())
  );
}

/**
 * Write generated rubric comments onto section, skipping fields the teacher
 * has edited unless force is set (regenerate). Returns true if anything
 * changed.
 */
export function applyRubricComments(section, populateData, force) {
  let changed = false;
  Object.keys(populateData || {}).forEach(function (componentType) {
    let field = COMPONENT_FIELD_MAP[componentType];
    let flag = field && AUTO_GENERATED_FLAG_MAP[field];
    if (!flag) return;
    if (!force && !canAutoPopulate(section, componentType)) return;
    section.setProperties({
      [field]: populateData[componentType],
      [flag]: true,
    });
    changed = true;
  });
  return changed;
}

/**
 * Build teacher comment, next steps and help at home HTML (keyed by
 * component type) from the rubric criteria matching the section's strand
 * judgements. Wording is personalised for the student, naming them as the
 * report template does. The narrative criteria are picked at random each
 * time; next steps and help at home come from the same criteria, so all
 * three match.
 */
export function generateRubricComments({
  store,
  student,
  reportTemplate,
  section,
  rubricElements,
  milestone,
}) {
  let populateData = {};
  if (!section) {
    return populateData;
  }

  let allCriteria = store.peekAll('reports/rubric-criteria');
  let sectionElements = section.get('elementProgressions') || [];
  let elements = rubricElements || [];

  // Gather criteria from ALL strands, grouped by strand for the teacher
  // comment narrative
  let strandCriteria = [];
  elements.forEach(function (element) {
    let elId = element.get('id');
    let entry = findBy(sectionElements, 'elementId', elId);
    let judgement = entry && entry.get('judgement');
    if (!judgement) {
      return;
    }

    let matched = allCriteria
      .filter(function (c) {
        return (
          c.belongsTo('elementId').id() === elId &&
          c.get('progressColumn') === parseInt(judgement, 10)
        );
      })
      .sort(byKeys('sequence'));

    if (matched.length) {
      strandCriteria.push(matched);
    }
  });
  let allStrandCriteria = [].concat(...strandCriteria);

  if (!allStrandCriteria.length) {
    return populateData;
  }

  let firstName = reportStudentName(student, reportTemplate).firstName;
  let safeName = escapeHtml(firstName);
  let gender = student ? student.get('preferredGender') : 'N';
  let pronoun = gender === 'M' ? 'He' : gender === 'F' ? 'She' : 'They';

  // Criteria wording with the student's name and pronouns filled in,
  // deduplicated against everything already collected into seen. Stops at
  // limit items and collects the criteria used into picked.
  let personalisedUnique = function (criteria, field, seen, limit, picked) {
    let items = [];
    for (let c of criteria) {
      if (items.length === limit) break;
      let text = (c.get(field) || '').trim();
      if (!text && field === 'achievedNarrative') {
        text = (c.get('text') || '').trim();
      }
      text = personaliseRubricText(text, student, reportTemplate).trim();
      let key = text.toLowerCase();
      if (!text || seen.has(key)) continue;
      seen.add(key);
      items.push(text);
      picked?.push(c);
    }
    return items;
  };

  // Teacher comment: build a natural narrative from a random selection of
  // fragments in every strand (deduplicated). The criteria picked here also
  // give the next steps and help at home.
  let narrativeSeen = new Set();
  let pickedCriteria = [];
  let strandFragments = strandCriteria
    .map(function (criteria) {
      return personalisedUnique(
        shuffle(criteria),
        'achievedNarrative',
        narrativeSeen,
        NARRATIVE_FRAGMENTS_PER_STRAND,
        pickedCriteria,
      );
    })
    .filter(function (fragments) {
      return fragments.length;
    });

  // Empty when no criteria have narrative wording, so a generated comment
  // from an earlier pick is cleared with its lists
  populateData['teacher-comment'] = '';
  if (strandFragments.length) {
    // Opening sentence based on overall judgement and whether the milestone
    // is interim (mid-year) or final (end of year).
    let overallJudgement = section.get('judgement');
    let opening = '';
    if (firstName && overallJudgement) {
      let j = parseInt(overallJudgement, 10);
      let isInterim = !!(milestone && milestone.get('isInterim'));
      let isOnTrack = isInterim ? j >= 3 : j >= 4;
      // The template's wording (or the default), escaped as it's plain text,
      // with the student's name and pronouns filled in
      let statement = personaliseRubricText(
        escapeHtml(judgementWording(reportTemplate, isInterim, isOnTrack)),
        student,
        reportTemplate,
      )
        .trim()
        .replace(/[.!?]+$/, '');
      opening = '<p><strong>' + statement + '.</strong></p>';
    }

    // Narrative wording is written without a subject ("responds to texts by
    // sharing simple reactions. is developing confidence ..."), so every
    // sentence opens with the student's pronoun. Fragments come from rubric
    // criteria that are stored HTML-encoded, so embed as-is; escaping again
    // would double-encode entities like &#39;.
    let withSubject = function (sentence) {
      let text = sentence.replace(/[.,;:!?]+$/, '').trim();
      if (!text) return '';
      // Some wording already opens with the student's name (via {name} /
      // FIRSTNAME) or pronoun (via HE/SHE/THEY), filled in above
      if (
        (safeName && text.startsWith(safeName + ' ')) ||
        text.toLowerCase().startsWith(pronoun.toLowerCase() + ' ')
      ) {
        return text + '.';
      }
      return pronoun + ' ' + text.charAt(0).toLowerCase() + text.slice(1) + '.';
    };

    // One paragraph per strand
    let paragraphs = strandFragments.map(function (fragments) {
      let sentences = [];
      fragments.forEach(function (fragment) {
        fragment
          .replace(/\s+/g, ' ')
          .trim()
          .split(SENTENCE_BREAK)
          .forEach(function (sentence) {
            let text = withSubject(sentence);
            if (text) sentences.push(text);
          });
      });
      return '<p>' + sentences.join(' ') + '</p>';
    });

    populateData['teacher-comment'] = opening + paragraphs.join('');
  }

  // Next steps wording mostly reads "to retell events ...", so capitalise
  // each item to read as a list entry
  let bulletList = function (items) {
    return (
      '<ul>' +
      items
        .map(function (item) {
          return (
            '<li>' + item.charAt(0).toUpperCase() + item.slice(1) + '</li>'
          );
        })
        .join('') +
      '</ul>'
    );
  };

  // Next steps and help at home: bullet lists from the narrative's criteria
  // (deduplicated). Empty when those criteria have no wording, so generated
  // lists from an earlier pick are cleared.
  let nextStepItems = personalisedUnique(pickedCriteria, 'nextStep', new Set());
  populateData['next-steps'] = nextStepItems.length
    ? bulletList(nextStepItems)
    : '';
  let helpAtHomeItems = personalisedUnique(
    pickedCriteria,
    'helpAtHome',
    new Set(),
  );
  populateData['help-at-home'] = helpAtHomeItems.length
    ? bulletList(helpAtHomeItems)
    : '';

  return populateData;
}

/**
 * generateRubricComments for callers that only have the section's subject
 * (e.g. the regenerate action), resolving the rubric elements (from the
 * section's rubric family) and current milestone the same way the progress
 * panel does.
 */
export function generateRubricCommentsForSubject({
  store,
  subject,
  student,
  reportTemplate,
  section,
  rolloverMonthDate,
  rubricFamilyId,
}) {
  if (!subject || !student) {
    return {};
  }
  return generateRubricComments({
    store,
    student,
    reportTemplate,
    section,
    rubricElements: resolveRubricElements(
      subject,
      student,
      store,
      rolloverMonthDate,
      rubricFamilyId,
    ),
    milestone: getCurrentMilestone(
      subject.get('milestones') || [],
      student,
      rolloverMonthDate,
    ),
  });
}
